import { Component, Input, OnChanges } from '@angular/core';
import { PoChartOptions, PoChartSerie, PoChartType } from '@po-ui/ng-components';

import { formatPeriodDate, Period } from 'src/app/menu/smart-review/core/utils/period.util';
import { withSinglePointTooltip } from 'src/app/menu/smart-review/core/utils/po-chart-tooltip.util';
import { ReviewStatsPoint } from '../../interfaces/review-stats.interface';
import { integerAxis } from '../../utils/chart-axis.util';
import { formatApiDateShort } from '../../utils/review-stats-format.util';

/**
 * Acima disso o eixo X (uma categoria por dia) sobrepõe os rótulos de data e vira
 * ilegível — mesmo limiar e mesmo motivo de `CostsTimeseriesChartComponent`.
 */
const WEEKLY_THRESHOLD = 31;

const WEEKLY_NOTE = 'Por semana — o período tem dias demais para um a um.';

/**
 * Evolução do volume de reviews por dia (ou por semana, em períodos longos). Duas
 * séries na mesma coluna — pedidos e reviews com achados — para mostrar se o volume
 * que chega também está trazendo código que exige atenção.
 *
 * Estrutura, agrupamento semanal e aviso de pontas parciais espelham
 * `CostsTimeseriesChartComponent` — mesmo problema (dias sem dado não vêm do backend,
 * período longo ilegível em barra diária), resolvido da mesma forma.
 */
@Component({
  selector: 'app-review-stats-timeseries-chart',
  templateUrl: './review-stats-timeseries-chart.component.html'
})
export class ReviewStatsTimeseriesChartComponent implements OnChanges {
  @Input() points: ReviewStatsPoint[] = [];

  /** O recorte da tela — só ele diz quais baldes semanais são parciais. Ver `groupingNote`. */
  @Input() period?: Period;

  readonly columnType = PoChartType.Column;

  categories: string[] = [];
  series: PoChartSerie[] = [];
  chartOptions: PoChartOptions = {};
  weekly = false;
  groupingNote = '';

  ngOnChanges(): void {
    const points = this.points ?? [];
    this.weekly = points.length > WEEKLY_THRESHOLD;

    const [categories, requested, withFindings, weekStarts] = this.weekly ? weeklyBuckets(points) : dailyBuckets(points);
    this.categories = categories;
    this.series = requested.length
      ? [
          withSinglePointTooltip({ label: 'Pedidos', data: requested }, requested.length === 1 ? `${requested[0]}` : undefined),
          withSinglePointTooltip(
            { label: 'Reviews com achados', data: withFindings },
            withFindings.length === 1 ? `${withFindings[0]}` : undefined
          )
        ]
      : [];
    this.chartOptions = integerAxis([...requested, ...withFindings]);
    this.groupingNote = this.weekly ? groupingNote(weekStarts, this.period) : '';
  }

  get hasData(): boolean {
    return this.series.length > 0;
  }
}

/**
 * Categorias, valores das duas séries e — só no agrupamento semanal — a segunda-feira
 * de cada balde. As datas de início vêm cruas (`YYYY-MM-DD`), não formatadas, pelo
 * mesmo motivo de `CostsTimeseriesChartComponent`: `groupingNote` compara contra elas.
 */
type Buckets = [categories: string[], requested: number[], withFindings: number[], weekStarts: string[]];

function dailyBuckets(points: ReviewStatsPoint[]): Buckets {
  return [
    points.map(point => formatApiDateShort(point.date)),
    points.map(point => point.reviews),
    points.map(point => point.reviewsWithFindings),
    []
  ];
}

/**
 * Agrupa por semana (segunda a domingo) somando pedidos e reviews com achados dos dias dentro
 * dela. `new Date(ano, mês, dia)` a partir dos componentes já separados é seguro — o
 * que quebraria seria parsear a string inteira (`new Date(iso)`), que o JS interpreta
 * como UTC e recua um dia em UTC-3.
 */
function weeklyBuckets(points: ReviewStatsPoint[]): Buckets {
  const order: string[] = [];
  const requestedTotals = new Map<string, number>();
  const withFindingsTotals = new Map<string, number>();

  for (const point of points) {
    const date = parseApiDate(point.date);
    const mondayOffset = (date.getDay() + 6) % 7; // 0=segunda ... 6=domingo
    date.setDate(date.getDate() - mondayOffset);
    const weekStart = toApiDate(date);

    if (!requestedTotals.has(weekStart)) {
      order.push(weekStart);
      requestedTotals.set(weekStart, 0);
      withFindingsTotals.set(weekStart, 0);
    }
    requestedTotals.set(weekStart, requestedTotals.get(weekStart)! + point.reviews);
    withFindingsTotals.set(weekStart, withFindingsTotals.get(weekStart)! + point.reviewsWithFindings);
  }

  const categories = order.map(weekStart => `Sem. ${formatApiDateShort(weekStart)}`);
  return [
    categories,
    order.map(weekStart => requestedTotals.get(weekStart)!),
    order.map(weekStart => withFindingsTotals.get(weekStart)!),
    order
  ];
}

/**
 * O aviso do agrupamento semanal, mais a ressalva das pontas quando o recorte corta
 * uma semana no meio. Mesma lógica (e mesmos motivos) de `CostsTimeseriesChartComponent`'s
 * `groupingNote`, adaptada para não depender de custo.
 *
 * Exportada (e com `today` como parâmetro) para o spec fixar o dia — o corte da última
 * semana depende de quando se olha.
 */
export function groupingNote(weekStarts: string[], period?: Period, today = formatPeriodDate(new Date())): string {
  if (!weekStarts.length) { return WEEKLY_NOTE; }

  const from = period?.dateFrom ?? '';
  const to = period?.dateTo && period.dateTo < today ? period.dateTo : today;

  const firstStart = weekStarts[0];
  const lastStart = weekStarts[weekStarts.length - 1];
  const firstEnd = addDays(firstStart, 6);
  const lastEnd = addDays(lastStart, 6);

  const firstPartial = !!from && from > firstStart;
  const lastPartial = to < lastEnd;

  const first = `${formatApiDateShort(from)} a ${formatApiDateShort(firstEnd)}`;
  const last = `${formatApiDateShort(lastStart)} a ${formatApiDateShort(to)}`;

  if (firstPartial && lastPartial) {
    return `${WEEKLY_NOTE} As pontas cobrem menos dias: a primeira só ${first}, a última só ${last}.`;
  }
  if (lastPartial) {
    return `${WEEKLY_NOTE} A última cobre só ${last}.`;
  }
  if (firstPartial) {
    return `${WEEKLY_NOTE} A primeira cobre só ${first}.`;
  }
  return WEEKLY_NOTE;
}

/** `YYYY-MM-DD` como data local. `new Date(iso)` seria UTC e recuaria um dia em UTC-3. */
function parseApiDate(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** Componentes locais (ano/mês/dia) de volta para `YYYY-MM-DD` — sem passar por UTC. */
function toApiDate(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Soma dias a uma data `YYYY-MM-DD`, de volta em `YYYY-MM-DD`. */
function addDays(value: string, days: number): string {
  const date = parseApiDate(value);
  date.setDate(date.getDate() + days);
  return toApiDate(date);
}
